import { useEffect, useRef, useState } from 'react';
import type { AdminAiOverview, AiModel } from '@kassa/shared';
import { adminApi, AdminApiError } from '../../api/admin';
import { money } from './Billing';
import { normMoney } from './Packages';

// Sistem → AI (KT-72 hissə 4): model, obyekt başına aylıq limit (susmaya görə 5 ₼), şirkət üzrə aç/bağla və öz limiti,
// ay üzrə istifadə (token və ₼). API açarı paneldə yazılmır və göstərilmir — yalnız «var / yoxdur».

function errText(err: unknown): string {
  return err instanceof AdminApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.';
}

export function AiSettings({ isOwner }: { isOwner: boolean }) {
  const [month, setMonth] = useState(new Date(Date.now() + 4 * 3600_000).toISOString().slice(0, 7));
  const [data, setData] = useState<AdminAiOverview | null>(null);
  const [model, setModel] = useState<AiModel>('claude-haiku-5-5');
  const [limit, setLimit] = useState('5.00');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  // Gec gələn köhnə ay cavabı yenisinin üstünə yazılmasın; yazılmamış model/limit dəyişikliyi itməsin
  const seq = useRef(0);
  const touched = useRef(false);
  const setTouched = (v: boolean) => {
    touched.current = v;
  };

  function load() {
    const my = ++seq.current;
    adminApi
      .ai(month)
      .then((d) => {
        if (my !== seq.current) return;
        setData(d);
        if (!touched.current) {
          setModel(d.model);
          setLimit(d.defaultLimitAzn);
        }
      })
      .catch((err) => setMsg({ ok: false, text: errText(err) }));
  }
  useEffect(load, [month]);

  async function run(fn: () => Promise<void>, ok: string) {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      setMsg({ ok: true, text: ok });
      load();
    } catch (err) {
      setMsg({ ok: false, text: errText(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <section className="card card-wide">
        <h2>AI</h2>
        {msg && <p className={msg.ok ? 'ok' : 'bad'} role="status">{msg.text}</p>}
        {data && (
          <>
            <p className="small">
              API açarı: <strong>{data.keyPresent ? 'var' : 'yoxdur'}</strong> <span className="muted">(açar serverdə .env-dədir, burada göstərilmir)</span>
            </p>
            <div className="limit-grid">
              <label className="field">
                Model
                <select value={model} onChange={(e) => { setModel(e.target.value as AiModel); setTouched(true); }} disabled={!isOwner || busy}>
                  <option value="claude-haiku-5-5">claude-haiku-5-5 (ucuz, sürətli)</option>
                  <option value="claude-sonnet-5-5">claude-sonnet-5-5 (güclü)</option>
                </select>
              </label>
              <label className="field">
                Obyekt başına aylıq limit, ₼
                <input inputMode="decimal" value={limit} onChange={(e) => { setLimit(e.target.value); setTouched(true); }} disabled={!isOwner || busy} />
              </label>
            </div>
            {isOwner && (
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-primary btn-small"
                  disabled={busy}
                  onClick={() => {
                    const l = normMoney(limit);
                    if (!l) return setMsg({ ok: false, text: 'Limit «5,00» formasında' });
                    setTouched(false);
                    void run(() => adminApi.aiSettings(model, l), 'Saxlanıldı.');
                  }}
                >
                  Saxla
                </button>
              </div>
            )}
          </>
        )}
      </section>
      <section className="card card-wide">
        <div className="toolbar">
          <h2>Şirkətlər üzrə istifadə</h2>
          <span className="topbar-spacer" />
          <label className="field inline-field">
            Ay
            <input type="month" lang="az" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          </label>
        </div>
        {data && data.tenants.length === 0 && <p className="muted">Şirkət yoxdur.</p>}
        {data && data.tenants.length > 0 && (
          <div className="table-scroll">
            <table className="plain">
              <thead>
                <tr>
                  <th>Şirkət</th>
                  <th className="num">Sorğu</th>
                  <th className="num narrow-hide">Token (giriş / çıxış)</th>
                  <th className="num">Xərc</th>
                  <th>Limit</th>
                  <th>AI</th>
                </tr>
              </thead>
              <tbody>
                {data.tenants.map((t) => (
                  <tr key={t.tenantId}>
                    <td>{t.tenantName}</td>
                    <td className="num">{t.requests}</td>
                    <td className="num narrow-hide nowrap">
                      {t.tokensIn.toLocaleString('az')} / {t.tokensOut.toLocaleString('az')}
                    </td>
                    <td className="num nowrap">{money(t.costAzn)}</td>
                    <td className="small">{t.limitAzn ? money(t.limitAzn) : 'ümumi'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn-small"
                        disabled={busy}
                        onClick={() => run(() => adminApi.aiTenant(t.tenantId, !t.enabled, t.limitAzn), t.enabled ? 'AI bağlandı.' : 'AI açıldı.')}
                      >
                        {t.enabled ? 'Bağla' : 'Aç'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
